import { action } from "../index";
import { CURRENT_PAGE, DASHBOARD_DATA, DASHBOARD_DATE, DASHBOARD_ERROR, LOADING_STATE } from "../types";
import { getToken } from "../../helpers/tokenHelper";
import { apiCall } from "../../utils/apiCall";

/**
 * Set current page
 *
 * @param route
 * @returns {(function(*): void)|*}
 */
export const setCurrentPage = (route) => (dispatch) => {
    dispatch(action(CURRENT_PAGE, route));
}

/**
 * Set loading state
 *
 * @param isLoading
 * @returns {(function(*): void)|*}
 */
export const setLoadingSate = (isLoading) => (dispatch) => {
    dispatch(action(LOADING_STATE, isLoading));
}

/**
 * Dashboard Data with retry logic
 * @param data
 * @param retryCount - Internal parameter to track retry attempts
 * @returns {(function(*): Promise<void>)|*}
 */
export const getDashboardData = (data, retryCount = 0) => async (dispatch) => {
    const MAX_RETRIES = 2;
    const RETRY_DELAY = 1000; // 1 second base delay

    dispatch(action(LOADING_STATE, true));
    try {
        const token = getToken();
        const headers = {
            Authorization: "Bearer ".concat(token),
        };
        const req = await apiCall.get(`/intelligrapi/dashboardcard/get-dashboard-data`, {
            params: data,
            headers,
        });
        const payload = req && req.data ? req.data : {};
        dispatch(action(DASHBOARD_DATA, payload));
        // Clear any previous errors on success
        dispatch(action(DASHBOARD_ERROR, null));
    } catch (e) {
        const isServerError = e.response && e.response.status >= 500;
        const shouldRetry = isServerError && retryCount < MAX_RETRIES;

        if (shouldRetry) {
            // Exponential backoff: wait longer with each retry
            const delay = RETRY_DELAY * Math.pow(2, retryCount);
            console.log(`Dashboard API failed (attempt ${retryCount + 1}/${MAX_RETRIES + 1}). Retrying in ${delay}ms...`);

            // Wait before retrying
            await new Promise(resolve => setTimeout(resolve, delay));

            // Retry with incremented count
            dispatch(action(LOADING_STATE, false));
            return dispatch(getDashboardData(data, retryCount + 1));
        } else {
            // Final failure - set error state and clear dashboard data
            const errorMessage = e.response?.data?.message || e.message || 'Failed to load dashboard data';
            console.error('Dashboard API error:', errorMessage);
            dispatch(action(DASHBOARD_ERROR, errorMessage));
            dispatch(action(DASHBOARD_DATA, null));
        }
    } finally {
        dispatch(action(LOADING_STATE, false));
    }
}

/**
 * Set Dashboard dates
 *
 * @param date
 * @returns {(function(*): Promise<void>)|*}
 */
export const setDashboardDate = (date) => async (dispatch) => {
    dispatch(action(DASHBOARD_DATE, date));
}
